import React, { useState } from "react";
import {
  Col,
  Row,
  Button,
  Layout,
  Typography,
  Descriptions,
  Image,
  Modal,
  message,
  Card,
} from "antd";
import dayjs from "dayjs";
import { FaArrowLeft, FaEdit, FaTrash } from "react-icons/fa";
import { ASSETS, UPLOADS_URL, BASE_URL } from "../../config/constants/api";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import axios from "axios";
import "../upcoming-lessons/upcoming-lessons.css";
import "../course-management/course-management.css";
import "../quiz-management/quiz-details.css";
import "./asset-management.css";

const { Title, Text, Paragraph } = Typography;

function AssetDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const token = useSelector((state) => state.user.userToken);
  const asset = location.state?.asset ?? null;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!asset) {
    return (
      <Layout
        className="configuration upcoming-lessons-page quiz-details-page"
        style={{ minHeight: "100vh" }}
      >
        <Row className="blueBackground-old coursebnr asset-management-hero">
          <Typography.Title
            level={1}
            className="fontFamily1 aboutbanner-content upcoming-lessons-hero-title"
          >
            Resource Library
          </Typography.Title>
        </Row>
        <div className="boxDetails2 quiz-details-shell" style={{ padding: "24px 16px 48px" }}>
          <Card className="quiz-details-panel" bordered={false}>
            <Paragraph className="quiz-details-bulk-intro" style={{ marginBottom: 16 }}>
              No asset data. Please open this page from the Asset list (View).
            </Paragraph>
            <Button type="primary" size="large" className="loginButton" onClick={() => navigate("/asset-management")}>
              Back to Assets
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }
  const handleDelete = () => {
    setDeleting(true);
    axios
      .delete(BASE_URL + ASSETS.deleteAsset + id, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res?.data ?? res;
        setDeleting(false);
        setDeleteModalOpen(false);
        if (data?.status) {
          swal("Success!", "Asset deleted", "success");
          navigate("/asset-management");
        } else {
          swal("Error", data?.message || "Delete failed", "error");
        }
      })
      .catch(() => {
        setDeleting(false);
        setDeleteModalOpen(false);
        message.error("Delete failed");
      });
  };
  const courses = asset.courses || [];

  return (
    <Layout
      className="configuration upcoming-lessons-page quiz-details-page"
      style={{ minHeight: "100vh" }}
    >
      <Row className="blueBackground-old coursebnr asset-management-hero">
        <Typography.Title
          level={1}
          className="fontFamily1 aboutbanner-content upcoming-lessons-hero-title"
        >
          Resource Library
        </Typography.Title>
      </Row>

      <div className="boxDetails2 quiz-details-shell">
        <Card className="quiz-details-top-card" bordered={false}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={14} lg={16}>
              <div className="course-management-title-block">
                <button
                  type="button"
                  className="course-management-inline-back"
                  aria-label="Go back"
                  onClick={() => navigate(-1)}
                >
                  <FaArrowLeft aria-hidden="true" />
                </button>
                <Typography.Title level={2} className="fontFamily1 quiz-details-page-title">
                  Asset Details
                </Typography.Title>
              </div>
            </Col>
            <Col xs={24} md={10} lg={8} className="quiz-management-actions-col">
              <div className="course-management-primary-actions">
                <Button
                  type="primary"
                  size="large"
                  className="loginButton"
                  icon={<FaEdit />}
                  onClick={() =>
                    navigate(`/asset-management/${id}/edit`, { state: { asset } })
                  }
                >
                  Edit
                </Button>
                <Button
                  danger
                  size="large"
                  icon={<FaTrash />}
                  onClick={() => setDeleteModalOpen(true)}
                >
                  Delete
                </Button>
              </div>
            </Col>
          </Row>
        </Card>

        <div className="quiz-details-panel" style={{ marginTop: 20 }}>
          <Title level={4} style={{ marginTop: 0 }}>
            {asset.title}
          </Title>
          {asset.description && (
            <Paragraph type="secondary" style={{ fontSize: 15 }}>
              {asset.description}
            </Paragraph>
          )}

          <Descriptions
            bordered
            column={1}
            size="small"
            className="asset-details-panel-card"
            style={{ marginTop: 16 }}
          >
              <Descriptions.Item label="Type">{asset.type || "—"}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Text strong style={{ textTransform: "uppercase" }}>{asset.status ?? "—"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {asset.createdAt ? dayjs(asset.createdAt).format("MMM D, YYYY h:mm A") : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {asset.updatedAt ? dayjs(asset.updatedAt).format("MMM D, YYYY h:mm A") : "—"}
              </Descriptions.Item>
            </Descriptions>

            {asset.fileUrl && (
              <>
                <Title level={5} style={{ marginTop: 28 }}>
                  Preview
                </Title>
                {asset.type === "IMAGE" && (
                  <div className="quiz-details-thumb-wrap" style={{ marginTop: 12 }}>
                    <Image
                      src={UPLOADS_URL + "/" + asset.fileUrl}
                      alt={asset.title}
                      style={{ maxWidth: 400, maxHeight: 300, objectFit: "contain" }}
                    />
                  </div>
                )}
                {asset.type === "VIDEO" && (
                  <div className="quiz-details-thumb-wrap" style={{ marginTop: 12 }}>
                    <video
                      src={UPLOADS_URL + "/" + asset.fileUrl}
                      controls
                      style={{ maxWidth: "100%", maxHeight: 400 }}
                    />
                  </div>
                )}
                {asset.type === "PDF" && (
                  <p style={{ marginTop: 12 }}>
                    <a
                      href={UPLOADS_URL + "/" + asset.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open PDF
                    </a>
                  </p>
                )}
                {!["IMAGE", "VIDEO", "PDF"].includes(asset.type) && (
                  <p style={{ marginTop: 12 }}>
                    <a
                      href={UPLOADS_URL + asset.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open file
                    </a>
                  </p>
                )}
              </>
            )}

            {courses.length > 0 && (
              <>
                <Title level={5} style={{ marginTop: 24 }}>Courses</Title>
                <Row gutter={[8, 8]}>
                  {courses.map((c) => {
                    const courseId = typeof c === "object" ? c._id : c;
                    const title = typeof c === "object" ? c.title || c.name : c;
                    return (
                      <Col key={courseId}>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => navigate(`/course-details/${courseId}`)}
                        >
                          {title || courseId}
                        </Button>
                      </Col>
                    );
                  })}
                </Row>
              </>
            )}
        </div>
      </div>

      <Modal
        title="Delete Asset"
        open={deleteModalOpen}
        onOk={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        confirmLoading={deleting}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        Are you sure you want to delete this asset?
      </Modal>
    </Layout>
  );
}

export default AssetDetails;
